// The alarm history timeline (Phase UI-2). Reads GET
// /v1/boats/:id/alerts/history — alertId-scoped on the detail page, boat-wide on
// the Alarms page's "Recent activity" disclosure. Rows render via the pure
// `toHistoryRowView` formatter (transition · human actor · time). Paging is
// explicit "Show more" (never infinite scroll): the next page is fetched with
// the oldest shown row's time as the inclusive `to` bound, then de-duplicated on
// the boundary row.

import { useCallback, useEffect, useState } from 'react';

import { api } from '../../api';
import {
  toHistoryRowView,
  type AlarmHistoryEntry,
  type AlarmHistoryResponse,
} from '../../lib/alarmDisplay';

interface AlarmHistoryListProps {
  boatId: string;
  /** Filter to one alert (detail page); omit for the boat-wide activity list. */
  alertId?: string;
  /** Rows per page (default 50). */
  pageSize?: number;
  /** Boat-wide list: map an alertId → a human alarm title to prefix the row. */
  titleForAlertId?: (alertId: string) => string | undefined;
  /** Stable testid for the list container. */
  testid?: string;
}

type HistoryState =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'ready'; entries: AlarmHistoryEntry[]; hasMore: boolean };

/** A stable-ish identity for a row, used only to de-dupe the paging boundary. */
function rowKey(e: AlarmHistoryEntry): string {
  return `${e.time}|${e.alertId}|${e.transition}`;
}

export function AlarmHistoryList({
  boatId,
  alertId,
  pageSize = 50,
  titleForAlertId,
  testid,
}: AlarmHistoryListProps) {
  const [state, setState] = useState<HistoryState>({ kind: 'loading' });
  const [loadingMore, setLoadingMore] = useState(false);

  const buildPath = useCallback(
    (to?: string): string => {
      const params = new URLSearchParams();
      if (alertId !== undefined) params.set('alertId', alertId);
      params.set('limit', String(pageSize));
      if (to !== undefined) params.set('to', to);
      return `/v1/boats/${boatId}/alerts/history?${params.toString()}`;
    },
    [boatId, alertId, pageSize]
  );

  useEffect(() => {
    let cancelled = false;
    setState({ kind: 'loading' });
    void (async () => {
      try {
        const data = await api<AlarmHistoryResponse>(buildPath());
        if (cancelled) return;
        setState({
          kind: 'ready',
          entries: data.entries,
          hasMore: data.entries.length >= pageSize,
        });
      } catch (err) {
        if (cancelled) return;
        const e = err as { message?: string };
        setState({ kind: 'error', message: e.message ?? 'Failed to load activity.' });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [buildPath, pageSize]);

  const loadMore = useCallback(async (): Promise<void> => {
    if (state.kind !== 'ready' || !state.hasMore || loadingMore) return;
    const oldest = state.entries[state.entries.length - 1];
    if (oldest === undefined) return;
    setLoadingMore(true);
    try {
      const data = await api<AlarmHistoryResponse>(buildPath(oldest.time));
      const seen = new Set(state.entries.map(rowKey));
      const merged = [...state.entries];
      let added = 0;
      for (const entry of data.entries) {
        const k = rowKey(entry);
        if (!seen.has(k)) {
          seen.add(k);
          merged.push(entry);
          added += 1;
        }
      }
      setState({
        kind: 'ready',
        entries: merged,
        // Only more to fetch if this page was full AND advanced the window.
        hasMore: added > 0 && data.entries.length >= pageSize,
      });
    } catch {
      // A failed "show more" just stops paging — the rows already shown stay.
      setState((s) => (s.kind === 'ready' ? { ...s, hasMore: false } : s));
    } finally {
      setLoadingMore(false);
    }
  }, [state, loadingMore, buildPath, pageSize]);

  if (state.kind === 'loading') {
    return <p className="muted">Loading activity…</p>;
  }
  if (state.kind === 'error') {
    return <p className="muted">{state.message}</p>;
  }
  if (state.entries.length === 0) {
    return (
      <p className="muted" data-testid={`${testid ?? 'alarm-history'}-empty`}>
        No activity recorded yet.
      </p>
    );
  }

  return (
    <div className="alarm-history" data-testid={testid ?? 'alarm-history'}>
      <ol className="alarm-history__list">
        {state.entries.map((entry, i) => {
          const row = toHistoryRowView(entry);
          const title = titleForAlertId?.(entry.alertId);
          return (
            <li
              key={`${row.time}-${entry.alertId}-${entry.transition}-${i}`}
              className="alarm-history__row"
            >
              {title !== undefined ? <span className="alarm-history__alarm">{title}</span> : null}
              <span className="alarm-history__line">
                {row.transition} · {row.actor} · {row.time}
              </span>
            </li>
          );
        })}
      </ol>
      {state.hasMore ? (
        <button
          type="button"
          className="alarm-history__more"
          onClick={() => void loadMore()}
          disabled={loadingMore}
          data-testid={`${testid ?? 'alarm-history'}-more`}
        >
          {loadingMore ? 'Loading…' : 'Show more'}
        </button>
      ) : null}
    </div>
  );
}
